import { supabase } from '@/core/supabase/client';

/**
 * Reportes.
 *
 * El reporte mensual sale de `work_list` (ver `works.ts`) y el resultado por
 * participante de `dashboard_summary` (ver `dashboard.ts`). Acá viven sólo las
 * dos lecturas propias de esta fase, para no tener dos copias de la misma
 * regla de privacidad.
 */

export interface ClientReportRow {
  client_id: string;
  client_name: string;
  /** `legacy_placeholder` no es un cliente: va fuera del ranking comercial (D5). */
  record_kind: 'commercial' | 'legacy_placeholder';
  establishment_id: string | null;
  establishment_name: string | null;
  works_count: number;
  total_kg: number;
  pending_count: number;
  /** `null` si algún trabajo del grupo no tiene precio. */
  total_cents: number | null;
  pending_to_bill_count: number;
  pending_to_collect_count: number;
  pending_to_collect_cents: number | null;
}

/** `month = null` devuelve el resumen del año entero (Spec §37). */
export async function fetchReportByClient(
  organizationId: string,
  year: number,
  month: number | null,
): Promise<ClientReportRow[]> {
  const { data, error } = await supabase.rpc('report_by_client', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return (data ?? []) as ClientReportRow[];
}

export interface ClosureExportWork {
  work_session_id: string;
  work_date: string;
  client_name: string;
  establishment_name: string | null;
  total_kg: number;
  steer_price_cents: number | null;
  honoraria_cents: number | null;
  mobility_cents: number | null;
  total_cents: number | null;
  services: string | null;
  /** Lo que se anotó como gastos del campo. Es lo que se descuenta al facturar. */
  description: string | null;
  /** Quién emite el comprobante de este trabajo. */
  biller_name: string | null;
  /** A quién le entra la plata. No siempre es el mismo que factura. */
  payment_recipient_name: string | null;
  /** Cómo se reparten los honorarios, en el orden en que se cargaron. */
  splits: { name: string; percentage: number }[];
  /** De quién es la movilidad. `null` cuando el trabajo no la cobra. */
  mobility_for: string | null;
}

export interface ClosureExport {
  version_id: string;
  version_no: number;
  is_current: boolean;
  closed_at: string;
  closed_by_name: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
  organization_id: string;
  organization_name: string;
  period_year: number;
  period_month: number;
  is_admin: boolean;
  /** Trabajos TAL COMO SE CONGELARON. No es un recálculo. */
  works: ClosureExportWork[];
  entries: {
    participant_id: string;
    display_name: string;
    earned_honoraria_cents: number;
    earned_mobility_cents: number;
    adjustments_cents: number;
    billed_cents: number;
    balance_cents: number;
  }[];
  transfers: { from_name: string; to_name: string; amount_cents: number }[];
}

/**
 * Lee una versión de cierre desde `closure_work_snapshots`.
 *
 * Se pide por id de versión, no por período: por eso una versión anterior sigue
 * siendo exportable después de reabrir y volver a cerrar, y produce siempre el
 * mismo documento aunque hoy el precio de referencia sea otro.
 */
export async function fetchClosureExport(versionId: string): Promise<ClosureExport> {
  const { data, error } = await supabase.rpc('closure_export', { p_version: versionId });
  if (error) throw error;
  return data as ClosureExport;
}
